import React from 'react';

const LOGO = 'https://customer-assets-wrfwihn1.emergentagent.net/job_aac32766-5fa5-4a83-81c9-03bafeaebc9e/artifacts/bi1rz20k_loyerbook.png';

/**
 * Global error boundary — catches any runtime error in the React tree
 * and displays a friendly message instead of a blank white page.
 * Also surfaces missing `REACT_APP_BACKEND_URL` build-time misconfiguration.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[LoyerBook] Uncaught error at root:', error, info);
    this.setState({ info });
  }

  handleReload = () => {
    // Wipe caches + SW then reload — recovers from broken cached versions.
    try {
      if ('caches' in window) {
        caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => r.unregister()));
      }
    } catch { /* no-op */ }
    setTimeout(() => window.location.reload(), 200);
  };

  render() {
    if (!this.state.error) return this.props.children;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const missingBackendUrl = !backendUrl || backendUrl === 'undefined';
    const message = this.state.error?.message || String(this.state.error);

    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        background: '#F8FAFC',
        color: '#0D1B2A',
      }}>
        <div style={{
          maxWidth: 560,
          width: '100%',
          background: 'white',
          borderRadius: 16,
          border: '1px solid #E2E8F0',
          padding: '32px 28px',
          boxShadow: '0 8px 32px rgba(15,23,42,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <img src={LOGO} alt="LoyerBook" style={{ width: 40, height: 40 }} />
            <div>
              <div style={{ fontWeight: 800, fontSize: 20 }}>LoyerBook</div>
              <div style={{ fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.15em' }}>Erreur inattendue</div>
            </div>
          </div>

          <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 12px' }}>
            L&apos;application n&apos;a pas pu démarrer.
          </h1>

          {missingBackendUrl && (
            <div style={{ padding: 14, borderRadius: 10, background: '#FEF3C7', border: '1px solid #FCD34D', color: '#78350F', fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>
              <strong>Configuration API incomplète.</strong>{' '}
              <code style={{ background: '#FDE68A', padding: '1px 6px', borderRadius: 4 }}>REACT_APP_BACKEND_URL</code>{' '}
              n&apos;a pas été définie au moment du build.
            </div>
          )}

          <div style={{ padding: 14, borderRadius: 10, background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#7F1D1D', fontSize: 13, marginBottom: 16, fontFamily: 'ui-monospace, monospace' }}>
            {message}
          </div>

          <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, margin: '0 0 20px' }}>
            Essayez de vider le cache et rafraîchir la page. Si le problème persiste, contactez le support à
            <a href="mailto:aide@loyerbook.com" style={{ color: '#1677FF', textDecoration: 'none', marginLeft: 4 }}>aide@loyerbook.com</a>.
          </p>

          <button
            onClick={this.handleReload}
            style={{
              width: '100%', height: 44, border: 'none', borderRadius: 10,
              background: '#1677FF', color: 'white', fontWeight: 600, cursor: 'pointer',
              fontSize: 14,
            }}
          >
            Vider le cache et recharger
          </button>

          <details style={{ marginTop: 16, fontSize: 12, color: '#64748B' }}>
            <summary style={{ cursor: 'pointer' }}>Détails techniques</summary>
            <pre style={{
              margin: '8px 0 0', padding: 10, background: '#F1F5F9', borderRadius: 8,
              fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
{`URL: ${window.location.href}
REACT_APP_BACKEND_URL: ${backendUrl || '(non défini)'}
Erreur: ${message}
${this.state.info?.componentStack || ''}`}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
